import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import type { PlanPriceHistory } from "@/lib/types";

/**
 * Compact per-flight price-history sparkline: a small inline SVG of the price
 * series over time (oldest→newest) plus a trend chip. Renders nothing when
 * there are fewer than two points (honest — no fake history). No chart library.
 *
 * FACTS vs PIXELS: the drawn line comes from `history.series` (a day-bucketed
 * downsample spanning the whole tracked period); the RANGE and the "down from
 * $X" label come from `history.summary`, which is aggregated over every
 * recorded check. The series is lossy by design, so it must never supply a
 * quoted price.
 */
export default function PriceHistorySparkline({
  history,
  stretch = false,
}: {
  history?: PlanPriceHistory | null;
  /** Fill the available row width (full-width horizontal history). */
  stretch?: boolean;
}) {
  const pts = (history?.series ?? []).filter((p) => Number.isFinite(p.price));
  if (pts.length < 2) return null;

  const prices = pts.map((p) => p.price);
  const summary = history?.summary ?? null;
  // Quoted figures: full-history aggregates only.
  const first = summary?.firstPrice ?? null;
  const last = summary?.now ?? null;
  const lo = summary?.low ?? null;
  const hi = summary?.high ?? null;
  // Geometry only: the polyline must scale to the points it actually draws.
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;

  const W = 132;
  const H = 22;
  const pad = 2;
  const stepX = (W - pad * 2) / (pts.length - 1);
  const coords = prices.map((p, i) => {
    const x = pad + i * stepX;
    const y = pad + (1 - (p - min) / span) * (H - pad * 2);
    return [x, y] as const;
  });

  const known = first != null && last != null;
  const dropped = known && last! < first!;
  const rose = known && last! > first!;
  const stroke = dropped ? "rgb(4 120 87)" : rose ? "rgb(180 83 9)" : "rgb(165 162 154)";
  const Icon = dropped ? TrendingDown : rose ? TrendingUp : Minus;
  // "$X" here is the FIRST-EVER recorded price, so the label names that period
  // rather than implying a recent move.
  const label = dropped
    ? `down from $${first} since tracking began`
    : rose
      ? `up from $${first} since tracking began`
      : known
        ? "unchanged since tracking began"
        : "price history";
  const range = lo != null && hi != null ? `$${lo}–$${hi}` : null;
  const [lx, ly] = coords[coords.length - 1];

  return (
    <div className={"flex items-center gap-2 " + (stretch ? "w-full min-w-0" : "mt-1 pl-1")}>
      <svg
        {...(stretch ? {} : { width: W })}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio={stretch ? "none" : "xMidYMid meet"}
        className={stretch ? "h-[22px] min-w-0 flex-1" : "shrink-0"}
        role="img"
        aria-label={
          range ? `Price history: ${label}, ranging ${range}` : `Price history: ${label}`
        }
      >
        <polyline
          points={coords.map(([x, y]) => `${x},${y}`).join(" ")}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {!stretch && <circle cx={lx} cy={ly} r="2.2" fill={stroke} />}
      </svg>
      <span className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0" style={{ color: stroke }} aria-hidden="true" />
        {range && <span className="font-mono tabular-nums">{range}</span>}
        <span className="text-muted-foreground/70">· {label}</span>
      </span>
    </div>
  );
}
